/**
 * EXIF orientation reader — parses the Orientation tag (0x0112) out of
 * a JPEG's APP1 EXIF segment WITHOUT pulling in a full EXIF library.
 * Most modern phones store the sensor's native orientation and expect
 * the viewer to rotate on display; browsers only honour this when
 * rendering `<img>` tags, but `<canvas>.drawImage` ignores it — so a
 * shot that looks upright in Photos ends up sideways once we crop.
 *
 * Reading the tag ourselves lets us pre-rotate the crop preview so
 * users don't have to tap Rotate on every photo pulled from a phone.
 *
 * Returns an EXIF orientation value (1–8). Falls back to 1 (upright)
 * on any parse error or when the tag is missing.
 */
export async function readExifOrientation(fileOrBlob) {
  try {
    if (!fileOrBlob || typeof fileOrBlob.slice !== 'function') return 1;
    // First 128 KB is more than enough — EXIF lives at the head of a
    // JPEG right after the SOI marker.
    const buf = await fileOrBlob.slice(0, 128 * 1024).arrayBuffer();
    const view = new DataView(buf);
    if (view.byteLength < 4) return 1;
    // JPEG start-of-image (SOI): 0xFFD8. Non-JPEG → assume upright.
    if (view.getUint16(0) !== 0xFFD8) return 1;
    let offset = 2;
    while (offset < view.byteLength - 4) {
      const marker = view.getUint16(offset);
      offset += 2;
      if (marker === 0xFFE1) { // APP1 (EXIF)
        const segSize = view.getUint16(offset);
        offset += 2;
        // "Exif\0\0" identifier
        if (view.getUint32(offset) !== 0x45786966) {
          offset += segSize - 2;
          continue;
        }
        const tiffStart = offset + 6;
        const littleEndian = view.getUint16(tiffStart) === 0x4949; // "II"
        const firstIfdOffset = view.getUint32(tiffStart + 4, littleEndian);
        const ifd0 = tiffStart + firstIfdOffset;
        if (ifd0 + 2 > view.byteLength) return 1;
        const numEntries = view.getUint16(ifd0, littleEndian);
        for (let i = 0; i < numEntries; i++) {
          const entry = ifd0 + 2 + i * 12;
          if (entry + 10 > view.byteLength) break;
          const tag = view.getUint16(entry, littleEndian);
          if (tag === 0x0112) {
            const o = view.getUint16(entry + 8, littleEndian);
            return (o >= 1 && o <= 8) ? o : 1;
          }
        }
        return 1;
      } else if ((marker & 0xFF00) !== 0xFF00) {
        // Not a valid marker — bail out.
        return 1;
      } else {
        const segSize = view.getUint16(offset);
        if (segSize < 2) return 1;
        offset += segSize;
      }
    }
    return 1;
  } catch {
    return 1;
  }
}

/**
 * Map an EXIF orientation value (1–8) to the CSS rotation (in degrees,
 * multiples of 90) that would make the image appear upright when
 * applied on top of the raw bitmap.
 *
 * Mirrored orientations (2, 4, 5, 7) can't be fixed with rotation
 * alone, so we treat them as "closest non-mirrored rotation" — good
 * enough for the common phone-photo case.
 */
export function orientationToRotation(orientation) {
  switch (orientation) {
    case 3:
    case 4:
      return 180;
    case 5:
    case 6:
      return 90;
    case 7:
    case 8:
      return 270;
    default:
      return 0;
  }
}
